import { Link } from "react-router-dom";
import { Boxes, Activity } from "lucide-react";
import { useApi } from "../lib/api";
import { shortAddr, timeAgo, fmtNum } from "../lib/format";
import { SectionHead, DataBadge, Skel, ErrorState } from "../components/common";
import usePageMeta from "../lib/usePageMeta";

export default function Explorer() {
  usePageMeta("Explorer", "Live Robinhood Chain explorer: latest blocks, transactions, gas prices and network statistics.");
  const { data: stats } = useApi("/explorer/stats", { refreshInterval: 30000 });
  const { data: blocks } = useApi("/explorer/blocks", { refreshInterval: 15000 });
  const { data: txs } = useApi("/explorer/txs", { refreshInterval: 15000 });
  const s = stats?.data || {};

  return (
    <div className="space-y-4">
      <SectionHead title="Robinhood Chain Explorer" badge={<DataBadge live={stats?.live ?? false} />} />
      {stats && !stats.live && <ErrorState msg="Live RPC/Blockscout temporarily unreachable — retrying automatically." />}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="network-stats">
        {[["Total Blocks", fmtNum(parseInt(s.total_blocks || 0))], ["Total Txs", fmtNum(parseInt(s.total_transactions || 0))],
          ["Total Addresses", fmtNum(parseInt(s.total_addresses || 0))], ["Avg Block Time", s.average_block_time ? `${(s.average_block_time / 1000).toFixed(1)}s` : "—"],
          ["Txs Today", fmtNum(parseInt(s.transactions_today || 0))], ["Gas Price", s.gas_price_wei ? `${(s.gas_price_wei / 1e9).toFixed(3)} gwei` : (s.gas_prices?.average?.price != null ? `${s.gas_prices.average.price} gwei` : "—")]].map(([l, v]) => (
          <div key={l} className="t-card p-3"><div className="text-[10px] uppercase text-[#6B7280]">{l}</div><div className="num text-base mt-0.5">{v}</div></div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div>
          <SectionHead title="Latest Blocks" badge={<DataBadge live={blocks?.live ?? false} />} />
          <div className="t-card" data-testid="latest-blocks">
            {!blocks ? <Skel rows={8} h={34} /> : blocks.items.length === 0 ? <ErrorState msg="Block feed unavailable." /> :
              blocks.items.slice(0, 12).map((b) => (
                <Link key={b.height} to={`/explorer/block/${b.height}`} className="flex items-center gap-3 px-3 py-2 border-b border-[#1a1a1a] last:border-0 hover:bg-[#1A1A1A] transition-colors" data-testid={`block-row-${b.height}`}>
                  <Boxes size={14} className="text-[#059669] shrink-0" />
                  <span className="num text-[13px] text-[#10B981]">#{b.height}</span>
                  <span className="text-xs text-[#6B7280]">{b.transactions_count ?? b.transaction_count ?? 0} txs</span>
                  <span className="num text-xs text-[#6B7280]">{shortAddr(b.miner?.hash || "", 4)}</span>
                  <span className="ml-auto num text-xs text-[#6B7280]">{b.timestamp ? timeAgo(new Date(b.timestamp).getTime() / 1000) : ""} ago</span>
                </Link>
              ))}
          </div>
        </div>
        <div>
          <SectionHead title="Latest Transactions" badge={<DataBadge live={txs?.live ?? false} />} />
          <div className="t-card" data-testid="latest-txs">
            {!txs ? <Skel rows={8} h={34} /> : txs.items.length === 0 ? <ErrorState msg="Transaction feed unavailable." /> :
              txs.items.slice(0, 12).map((t) => (
                <Link key={t.hash} to={`/explorer/tx/${t.hash}`} className="flex items-center gap-3 px-3 py-2 border-b border-[#1a1a1a] last:border-0 hover:bg-[#1A1A1A] transition-colors" data-testid={`tx-row-${t.hash.slice(2, 8)}`}>
                  <Activity size={14} className="text-[#059669] shrink-0" />
                  <span className="num text-[13px] text-[#10B981]">{shortAddr(t.hash, 6)}</span>
                  <span className="text-xs text-[#6B7280]">{t.method || "transfer"}</span>
                  <span className="num text-xs text-[#6B7280]">{(parseInt(t.value || "0") / 1e18).toFixed(4)} ETH</span>
                  <span className="ml-auto num text-xs text-[#6B7280]">{t.timestamp ? timeAgo(new Date(t.timestamp).getTime() / 1000) : ""} ago</span>
                </Link>
              ))}
          </div>
        </div>
      </div>
      <p className="text-[11px] text-[#6B7280]">Explorer data is served live from the Robinhood Chain RPC ({`rpc.mainnet.chain.robinhood.com`}) and Blockscout. Chain ID 4663.</p>
    </div>
  );
}
